import React from 'react';
import branchChangelog from './branch.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { BranchNodeState, NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType } from '../../';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Branch',
  identifier: 'Branch',
  command_id: 'logic.branch',
  icon: 'branch',
  background: '#FF7442',
  changelog: branchChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'branch.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'branch.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'True',
      id: 'branch.true',
      type: 'source' as HandleType,
      variant: 'conditional_execution',
    },
    {
      label: 'False',
      id: 'branch.false',
      type: 'source' as HandleType,
      variant: 'conditional_execution',
    },
    {
      label: 'Data',
      id: 'branch.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: BranchNodeState = {
  name: '',
  condition: {
    name: '',
    input: '',
    operator: 'equals',
    value: '',
  },
};

export const fields: NodeField[] = [
  {
    id: 'name',
    tab: '1',
    type: NodeFieldType.Input,
    default_type: NodeInputType.Text,
    label: 'Label',
    display_name: true,
    description: 'Use this field to give your Branch node a definitive name.',
    placeholder: 'Give your Branch node a name.',
    required: false,
  },
  {
    id: 'condition',
    type: NodeFieldType.Condition,
    label: 'Condition',
    description:
      'Use data from the graph to generate a boolean condition. If this condition is true, the true execution line will be executed next. Otherwise, the false execution line will be executed next.',
    placeholder: 'Input',
    required: true,
    tab: '1',
  },
];

export const output: NodeOutput = { default: null, dynamic: null };

export const Branch: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'logic.branch') return null;

  const displayTitle = command?.state?.name;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
